import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type CommissionRecoveryStatus =
  | 'PENDING'
  | 'PARTIAL'
  | 'SETTLED'
  | 'WAIVED'
  | 'CANCELLED';

export type CommissionRecoveryApplicationStatus = 'RESERVED' | 'APPLIED' | 'RELEASED';

export interface CommissionRecoveryApplicationItem {
  id: string;
  targetType: 'BOOKING_COMMISSION' | 'TOUR_COMMISSION';
  amount: number | string;
  status: CommissionRecoveryApplicationStatus;
  appliedAt: string | null;
  createdAt: string;
  bookingCommission: {
    commissionNo: string;
    booking: { id: string; bookingNo: string };
  } | null;
  tourCommission: {
    commissionNo: string;
    tour: { id: string; tourNo: string; tourName: string };
  } | null;
}

export interface CommissionRecoveryItem {
  id: string;
  originalAmount: number | string;
  recoveryAmount: number | string;
  settledAmount: number | string;
  remainingAmount: number | string;
  status: CommissionRecoveryStatus;
  reason: string;
  createdAt: string;
  updatedAt: string;
  recipientUser: {
    id: string;
    employeeCode: string;
    firstName: string;
    lastName: string;
  };
  booking: {
    id: string;
    bookingNo: string;
    tourName: string;
  };
  bookingRefund: {
    refundNo: string;
    refundType: 'PARTIAL' | 'FULL';
    amount: number | string;
    status: string;
  };
  bookingCommission: {
    commissionNo: string;
    totalAmount: number | string;
    recipientRole: 'SALE' | 'CARETAKER';
  };
  applications: CommissionRecoveryApplicationItem[];
}

@Injectable({ providedIn: 'root' })
export class CommissionRecoveryService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/commissions/recoveries`;

  async getAll(): Promise<CommissionRecoveryItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ recoveries: CommissionRecoveryItem[] }>(this.apiUrl, {
        withCredentials: true,
      }),
    );
    return response.recoveries;
  }
}
